import { fetchWithTimeout } from '@/hooks/helperFunctions';

/* eslint-disable no-undef */
import { getSessionStorage, setSessionStorage, deleteSessionStorage } from '@/utils/session';

interface IConfig {
  method: string;
  headers: HeadersInit;
  body?: BodyInit;
  mode: RequestMode | undefined;
  credentials: RequestCredentials | undefined;
  timeout?: number;
}

export const getProfileUrl = () => {
  const { origin, pathname, search, hash } = window.location;
  let profileUrl =
    process.env.NODE_ENV === 'production'
      ? 'https://profil.yousee.dk/'
      : 'https://profil.staging.internal.yousee.dk/';
  const newSearchParameter = (search ? '&' : '?') + 'rflp=true';
  const returnUrl = origin + pathname + search + newSearchParameter + hash;
  profileUrl += '?context=nsu-bb&returnUrl=' + encodeURIComponent(returnUrl);

  return profileUrl;
};

export const getLogoutUrl = () => {
  const { origin, pathname, search, hash } = window.location;
  let logoutUrl =
    process.env.NODE_ENV === 'production'
      ? 'https://id.yousee.dk/logout?timeout=3&returnUrl='
      : 'https://id-test.yousee.dk/logout?timeout=3&returnUrl=';
  const newSearchParameter = (search ? '&' : '?') + 'rflp=true';
  const returnUrl = origin + pathname + search + newSearchParameter + hash;
  logoutUrl += encodeURIComponent(returnUrl);

  return logoutUrl;
};

const getApiUrl = (useV2: boolean | undefined): string => {
  const windowParams = window.location.search;
  const version = useV2 ? 'v2' : 'v1';
  const endPoints = `https://test.ossfapi.swiss.np-nuuazure.yousee.dk/${version}`;
  const prodUrl = `https://oneshop-salesflow-api.yousee.dk/${version}`;

  let apiTestInstance: string | null = null;
  if (typeof URLSearchParams === 'function') {
    const searchParams = new URLSearchParams(windowParams);

    const apiTestInstanceFromSearchParam = searchParams.get('api');
    if (apiTestInstanceFromSearchParam === null) {
      const apiTestInstanceFromSessionStorage = getSessionStorage('apiTestInstance');
      if (apiTestInstanceFromSessionStorage !== null) {
        apiTestInstance = apiTestInstanceFromSessionStorage;
      }
    } else if (apiTestInstanceFromSearchParam === '') {
      deleteSessionStorage('apiTestInstance');
      apiTestInstance = null;
    } else {
      setSessionStorage('apiTestInstance', apiTestInstanceFromSearchParam);
      apiTestInstance = apiTestInstanceFromSearchParam;
    }
  }

  if (apiTestInstance !== null) {
    console.info(`You are using the ${apiTestInstance} instance of the Odin API`);
    if (apiTestInstance === 'local') {
      return `https://local.yousee.dk:5001/${version}`;
    }

    return `https://${apiTestInstance}.ossfapi.swiss.np-nuuazure.yousee.dk/${version}`;
  } else if (process.env.NODE_ENV === 'production') {
    return prodUrl;
  } else {
    return endPoints;
  }
};

const addTestSearchParam = function (url: string): string {
  if (typeof URLSearchParams === 'function') {
    const searchParams = new URLSearchParams(window.location.search);
    const testSearchParam: string | null = searchParams.get('test');
    if (testSearchParam !== null) {
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.search.slice(1));
      params.append('test', testSearchParam);
      urlObj.search = params.toString();
      url = urlObj.toString();
    }
  }
  return url;
};

type requestType = 'POST' | 'GET' | 'PUT' | 'DELETE' | 'PATCH';

const client = async function (
  endpoint: string,
  requestType: requestType,
  body?: BodyInit,
  useV2?: boolean,
  timeout?: number
): Promise<any> {
  const config: IConfig = {
    credentials: 'include',
    mode: 'cors',
    method: requestType,
    headers: new Headers({
      ...{
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    })
  };

  if (timeout) {
    config.timeout = timeout;
  }

  if (body) {
    config.body = JSON.stringify(body);
  }

  const baseURL = getApiUrl(useV2);
  const url = addTestSearchParam(`${baseURL}/${endpoint}`);

  const retryAttemps = 4;
  let counter = 0;

  let response = await fetchWithTimeout(url, config);

  while (counter <= retryAttemps) {
    if (response.ok && response.status === 200) {
      return response.json();
    } else if (response.status === 404) {
      return response;
    } else if (counter === retryAttemps) {
      throw 'error';
    } else {
      counter++;
      response = await fetchWithTimeout(url, config);
    }
  }
};

export { client };
